import { useState, useEffect } from "react";
import { useAuth } from "../../../provider";
import { Formik, Form, Field } from "formik";
import { useNavigate, useLocation } from "react-router-dom";
import * as Yup from "yup";
import axios from "axios";
import {
  Grid,
  Typography,
  CircularProgress,
  Box,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  IconButton,
  Divider,
  Stack,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { MapPin, Phone, Mail, FileText } from "lucide-react";

import * as c from "../../../constants";
import { pool } from "../../../constants";

import FormAlert from "../../../components/Alert/FormAlert";
import ButtonForm from "../../../components/Button/ButtonForm";
import DateInput from "../../../components/Forms/DateInput";
import TimeInput from "../../../components/Forms/TimeInput";
import CustomDialogSmall from "../../../components/Dialog/DiaCompSmall";
import PageProtectedOnboarding from "../../../components/Page/PageProtectedOnboarding";
import OnboardingSteps from "../../../components/Steps/OnboardingSteps";
import { useTranslation } from "../../../language";

const getValidationSchema = (t) =>
  Yup.object({
    laboratory: Yup.string().required(t("val_required")),
    visit_date: Yup.date()
      .min(new Date(), t("val_date_past"))
      .required(t("val_required")),
    visit_time: Yup.string().required(t("val_time_req")),
  });

export default function LabAppointment() {
  
  const { t, changeLanguage } = useTranslation();
  const theme = useTheme();
  const { state } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/OnboardingData";

  const url_lab = `${c.API_URL}/api/lab-visits/pre-schedule/`;

  // ✅ Form and data states
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState(null);

  // ✅ Labs data
  const [labs, setLabs] = useState([]);
  const [infoLab, setInfoLab] = useState(null);
  const [loadingLabs, setLoadingLabs] = useState(true);
  const [errorLabs, setErrorLabs] = useState(null);


useEffect(() => {
  if (state.user?.language) {
    changeLanguage(state.user.language);
  }
}, [state.user?.language]);

  // ----------------------------------------
  // Fetch laboratory options
  // ----------------------------------------
  useEffect(() => {
    const fetchLabs = async () => {
      try {
        const response = await axios.get(`${c.API_URL}/api/laboratories/`);
        setLabs(response.data);
      } catch (err) {
        console.error("Failed to fetch labs:", err);
        setErrorLabs(t("onb_lab_err_load"));
      } finally {
        setLoadingLabs(false);
      }
    };

    fetchLabs();
  }, []);

  // ----------------------------------------
  // Handle Form Submission
  // ----------------------------------------
  const handleSubmit = async (values, formikHelpers) => {
    try {
      setErrorMessage(null);
      setSuccessMessage(null);
      setLoadingSubmit(true);

      const response = await axios.patch(url_lab, values);

      if ([200, 201].includes(response.status)) {
        setSuccessMessage(t("onb_lab_success"));
        await axios.patch(`${c.API_URL}/api/onboarding/status/`, {
        onboarding_status: 3,
      });
        
        setTimeout(() => {
          navigate(from, { replace: true });
        }, 2000);
      }
    } catch (error) {
      if (error.response?.data) {
        const apiErrors = error.response.data;
        Object.keys(apiErrors).forEach((field) => {
          if (formikHelpers.setFieldError) {
            formikHelpers.setFieldError(field, apiErrors[field].join(" "));
          }
        });
        setErrorMessage({ message: t("val_form_fix") });
      } else {
        setErrorMessage({ message: t("val_server_error") });
      }
    } finally {
      setLoadingSubmit(false);
      formikHelpers.setSubmitting(false);
    }
  };

  // ----------------------------------------
  // Loading and data validation checks
  // ----------------------------------------
  if (loadingSubmit || loadingLabs) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PageProtectedOnboarding
      headerImage={pool}
      brandtext="Preventive Health Intelligence"
      headline={t("onb_lab00")}

      content={
        <>
          {/* Onboarding Steps */}
          <Grid container sx={{ my: 2, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Grid size={12} sx={{ width: '100%', maxWidth: 900, px: { xs: 1, sm: 2 }, display: "flex", justifyContent: "center", alignItems: "center" }}>
              <OnboardingSteps stepNumber={2} />
            </Grid>
          </Grid>

          {/* Instruction Text */}
          <Grid
            container
            spacing={2}
            sx={{
              mt: 4,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Grid size={12} sx={{ maxWidth: 600, px: { xs: 3, sm: 0 } }}>
              <Typography
                variant="subtitle1"
                textAlign="center"
                sx={{ fontWeight: 400 }}
              >
                {t("onb_lab01")}
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Box component="ul" sx={{ pl: 2.5, maxWidth: 600 }}>
                  {[t("onb_lab0a"), t("onb_lab0b"), t("onb_lab1")].map((item, i) => (
                    <Typography key={i} component="li" variant="subtitle1" sx={{ fontWeight: 400, mb: 0.5 }}>
                      {item}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>

           {/* Form Section */}
          <Grid
            container
            spacing={2}
            sx={{
              mt: 2,
              px: 2,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Grid size={{xs: 12}} sx={{maxWidth: 240}}>
              
                {errorLabs && (
                  <FormAlert
                    severity="error"
                    title={t("val_form_error")}
                    message={errorLabs}
                    onClose={() => setErrorLabs(null)}
                    sx={{ mb: 2, width: "100%" }}
                  />
                )}

                {errorMessage && (
                  <FormAlert
                    severity="error"
                    title={t("val_form_error")}
                    message={errorMessage}
                    onClose={() => setErrorMessage(null)}
                    sx={{ mb: 2, width: "100%" }}
                  />
                )}

                {successMessage && (
                  <FormAlert
                    severity="success"
                    title={t("val_success")}
                    message={successMessage}
                    onClose={() => setSuccessMessage(null)}
                    sx={{ mb: 2, width: "100%" }}
                  />
                )}

                <Formik
                  initialValues={{
                    laboratory: "",
                    visit_date: "",
                    visit_time: "",
                  }}
                  validationSchema={getValidationSchema(t)}
                  onSubmit={handleSubmit}
                >
                  {({ isSubmitting }) => (
                    <Form>
                      <Field name="laboratory">
                        {({ field, meta }) => (
                          <FormControl
                            fullWidth
                            size="small"
                            margin="normal"
                            error={meta.touched && Boolean(meta.error)}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: theme.palette.background.default,
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: theme.palette.grey[900],
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  borderColor: theme.palette.grey[900],
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                  borderColor: theme.palette.grey[900],
                                  borderWidth: 2,
                                },
                              },
                              "& .MuiInputLabel-root": {
                                color: theme.palette.text.secondary,
                                fontWeight: 400,
                                fontSize: "0.9rem",
                              },
                            }}
                          >
                            <InputLabel>{t("onb_lab5")}</InputLabel>
                            <Select
                              {...field}
                              label={t("onb_lab5")}
                              sx={{
                                color: theme.palette.mode === "dark"
                                  ? theme.palette.grey[100]
                                  : theme.palette.grey[900],
                                fontWeight: 400,
                              }}
                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    backgroundColor: theme.palette.mode === "dark"
                                      ? theme.palette.greydark.C800
                                      : theme.palette.grey.C200,
                                    borderRadius: 2,
                                  },
                                },
                              }}
                            >
                              <MenuItem value=""><em>{t("onb_lab5")}</em></MenuItem>
                              {labs.map((lab) => (
                                <MenuItem key={lab.id} value={lab.id}>
                                  <Box sx={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "space-between" }}>
                                    <span>{lab.name}</span>
                                    <span onClick={(e) => e.stopPropagation()}>
                                      <IconButton size="small" onClick={() => setInfoLab(lab)} sx={{ ml: 1 }}>
                                        <InfoOutlinedIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                            {meta.touched && meta.error && (
                              <FormHelperText>{meta.error}</FormHelperText>
                            )}
                          </FormControl>
                        )}
                      </Field>
                      <DateInput label={t("onb_lab3")} name="visit_date" />
                      <TimeInput label={t("onb_lab4")} name="visit_time" />

                      <ButtonForm
                        name={t("onb_labb")}
                        disabled={isSubmitting || loadingSubmit}
                      />
                    </Form>
                  )}
                </Formik>

                {infoLab && (
                  <CustomDialogSmall
                    open
                    onClose={() => setInfoLab(null)}
                    title={infoLab.name}
                    content={
                      <Stack spacing={2} sx={{ mt: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                          <MapPin size={22} color="#1976d2" style={{ marginTop: 2, flexShrink: 0 }} />
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 400 }}>
                              {t("onb_lab_info_address")}
                            </Typography>
                            <Typography variant="subtitle1" sx={{ fontWeight: 400 }}>
                              {[
                                infoLab.details?.address?.street,
                                infoLab.details?.address?.city,
                                infoLab.details?.address?.postal_code,
                                infoLab.details?.address?.country,
                              ].filter(Boolean).join(", ")}
                            </Typography>
                          </Box>
                        </Box>

                        {infoLab.details?.phone && (
                          <>
                            <Divider />
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                              <Phone size={22} color="#1976d2" style={{ flexShrink: 0 }} />
                              <Box>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 400 }}>
                                  {t("onb_lab_info_phone")}
                                </Typography>
                                <Typography variant="subtitle1" sx={{ fontWeight: 400 }}>
                                  {infoLab.details.phone}
                                </Typography>
                              </Box>
                            </Box>
                          </>
                        )}

                        {infoLab.details?.email && (
                          <>
                            <Divider />
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                              <Mail size={22} color="#1976d2" style={{ flexShrink: 0 }} />
                              <Box>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 400 }}>
                                  {t("onb_lab_info_email")}
                                </Typography>
                                <Typography variant="subtitle1" sx={{ fontWeight: 400 }}>
                                  {infoLab.details.email}
                                </Typography>
                              </Box>
                            </Box>
                          </>
                        )}

                        {infoLab.details?.notes && (
                          <>
                            <Divider />
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                              <FileText size={22} color="#1976d2" style={{ marginTop: 2, flexShrink: 0 }} />
                              <Box>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 400 }}>
                                  {t("onb_lab_info_notes")}
                                </Typography>
                                <Typography variant="subtitle1" sx={{ fontWeight: 400 }}>
                                  {infoLab.details.notes}
                                </Typography>
                              </Box>
                            </Box>
                          </>
                        )}
                      </Stack>
                    }
                  />
                )}
             
            </Grid>
          </Grid>

 
        </>
      }
    />
  );
}
